'use client'

/**
 * Top-level redirect for Shopify embedded apps.
 *
 * Navigating to an external URL (e.g. Shopify billing confirmation) from
 * inside the embedded admin iframe requires breaking out to the top window.
 * The Shopify Admin iframe carries `allow-top-navigation`, so direct
 * `window.top.location` works without needing App Bridge to broker.
 *
 * Order of attempts:
 *   1. Direct `window.top.location.href = url` — works for any iframe with
 *      the `allow-top-navigation` sandbox token (Shopify's). No App Bridge
 *      dependency, no postMessage, survives lost user activation across an
 *      awaited async mutation.
 *   2. App Bridge `window.open(url, '_top')` — falls back if the iframe
 *      blocks direct top-nav. Returns null silently when the parent's
 *      postMessage origin doesn't match (App Bridge limitation), so we
 *      explicitly check the return value and continue falling through.
 *   3. Same-window navigation — last resort; navigates the iframe instead
 *      of the top frame. Better than the click doing nothing.
 */
export function redirectTopLevel(url: string): void {
  if (typeof window === 'undefined') return

  // 1. Try direct top-level navigation first. This is the most reliable
  //    path — it doesn't depend on App Bridge being correctly initialised.
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = url
      return
    }
  } catch {
    // Cross-origin SecurityError — try App Bridge fallback
  }

  // 2. App Bridge bridge via window.open(_top). If the parent frame's
  //    postMessage origin is mismatched, this returns null silently — so
  //    we must check the return value to know whether to fall through.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const hasAppBridge = !!(window as any).shopify
  if (hasAppBridge) {
    try {
      const opened = window.open(url, '_top')
      if (opened !== null) return
    } catch {
      // Fall through
    }
  }

  // 3. Same-window navigation as a last resort. Won't break out of the
  //    iframe but at least the click is no longer a no-op.
  window.location.href = url
}
